import { X } from "lucide-react";
import { useCallback, useRef, useState } from "react";

import type { OpsSupportLaunchResponse } from "../../shared/operations-schemas";
import {
  AppMessageSchema,
  BootstrapProbeMessageSchema,
  LaunchGrantMessageSchema,
} from "../../shared/schemas";
import { Button } from "../components/ui/button";

export function SupportToolFrame({
  launch,
  onClose,
}: {
  launch: OpsSupportLaunchResponse;
  onClose: () => void;
}) {
  const frame = useRef<HTMLIFrameElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const registerHost = useCallback(
    (node: HTMLDivElement | null): (() => void) | undefined => {
      if (!node) return undefined;
      const listener = (event: MessageEvent<unknown>) => {
        const target = frame.current?.contentWindow;
        if (!target || event.source !== target || event.origin !== launch.technicalOrigin) return;
        const message = AppMessageSchema.safeParse(event.data);
        if (!message.success) return;
        if (message.data.type === "platform:app-session-expired") {
          setError("The support tool session expired. Close it and start a new support launch.");
          return;
        }
        if (message.data.appInstanceId !== launch.appInstanceId) return;
        target.postMessage(
          LaunchGrantMessageSchema.parse({
            type: "platform:launch-grant",
            appInstanceId: launch.appInstanceId,
            grant: launch.grant,
          }),
          launch.technicalOrigin,
        );
      };
      window.addEventListener("message", listener);
      return () => {
        window.removeEventListener("message", listener);
      };
    },
    [launch],
  );
  const probe = useCallback(() => {
    frame.current?.contentWindow?.postMessage(
      BootstrapProbeMessageSchema.parse({
        type: "platform:bootstrap-probe",
        appInstanceId: launch.appInstanceId,
      }),
      launch.technicalOrigin,
    );
  }, [launch]);
  return (
    <div className="space-y-3 rounded-sm border border-amber-500/50 p-3" ref={registerHost}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-semibold">Employee-attributed support tool session</p>
        <Button type="button" variant="outline" size="sm" onClick={onClose}>
          <X /> Close
        </Button>
      </div>
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      <iframe
        ref={frame}
        className="h-[70vh] w-full rounded-sm border bg-card"
        src={`${launch.technicalOrigin}/__platform/bootstrap?shellOrigin=${encodeURIComponent(window.location.origin)}`}
        title="Customer tool support session"
        sandbox="allow-scripts allow-same-origin allow-forms allow-downloads"
        onLoad={probe}
      />
    </div>
  );
}
